/**
 * In-memory storage implementation that mimics the localStorage API
 * Used as a fallback when localStorage is unavailable (private mode, disabled by policy, etc.)
 *
 * Note: Data is stored in memory and will be lost on page refresh.
 * This is acceptable for private browsing mode where users expect no persistence.
 */
export class MemoryStorage implements Storage {
  private data: Map<string, string> = new Map();

  /**
   * Returns the number of key/value pairs
   */
  get length(): number {
    return this.data.size;
  }

  /**
   * Returns the name of the nth key, or null if n is greater than or equal to the number of key/value pairs
   */
  key(index: number): string | null {
    const keys = Array.from(this.data.keys());
    return keys[index] ?? null;
  }

  /**
   * Returns the current value associated with the given key, or null if the given key does not exist
   */
  getItem(key: string): string | null {
    return this.data.get(key) ?? null;
  }

  /**
   * Sets the value of the pair identified by key to value, creating a new key/value pair if none existed for key previously
   */
  setItem(key: string, value: string): void {
    this.data.set(key, value);
  }

  /**
   * Removes the key/value pair with the given key, if a key/value pair with the given key exists
   */
  removeItem(key: string): void {
    this.data.delete(key);
  }

  /**
   * Removes all key/value pairs, if there are any
   */
  clear(): void {
    this.data.clear();
  }

  /**
   * Get the approximate size of storage in bytes
   * Used for quota management compatibility
   */
  getSize(): number {
    let total = 0;
    this.data.forEach((value, key) => {
      total += (value.length + key.length) * 2; // UTF-16 encoding
    });
    return total;
  }

  /**
   * Check if storage has any data
   */
  isEmpty(): boolean {
    return this.data.size === 0;
  }
}
